import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
  AnimatePresence,
  type MotionValue,
  type SpringOptions,
} from 'motion/react'
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'

// Adapted from reactbits.dev/components/Dock with two changes:
//  - Tailwind classes instead of a separate CSS file (matches project style).
//  - Vertical orientation (mouseY axis, height-based magnification) for
//    side-mounted docks — the reactbits original is horizontal-only.

const DOCK_BASE_SIZE = 48
const DOCK_MAGNIFICATION = 64
const DOCK_DISTANCE = 140
const DOCK_PANEL_PADDING = 8
const DOCK_GAP = 12

// Padding the main content needs to leave for a fixed-position dock so the
// magnified state never overlaps. = magnification + panel padding (top+bottom)
// + outer gap from the screen edge.
export const DOCK_OUTER_OFFSET = DOCK_MAGNIFICATION + DOCK_PANEL_PADDING * 2 + DOCK_GAP * 2

const DEFAULT_SPRING: SpringOptions = { mass: 0.1, stiffness: 150, damping: 12 }

export type Orientation = 'horizontal' | 'vertical'

export interface DockItemData {
  icon: ReactNode
  label: ReactNode
  onClick: () => void
  active?: boolean
  className?: string
}

export interface DockProps {
  items: DockItemData[]
  orientation: Orientation
  className?: string
}

interface DockItemProps {
  children: ReactNode
  onClick: () => void
  mouseAxis: MotionValue<number>
  orientation: Orientation
  reduce: boolean
  active?: boolean
  className?: string
}

function DockItem({
  children,
  onClick,
  mouseAxis,
  orientation,
  reduce,
  active,
  className = '',
}: DockItemProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isHovered = useMotionValue(0)

  // Distance from the mouse axis position to the item's center along the
  // active axis. The mouseAxis MotionValue holds the full pageX or pageY.
  const distance = useTransform(mouseAxis, val => {
    const rect = ref.current?.getBoundingClientRect()
    if (!rect) return Number.POSITIVE_INFINITY
    if (orientation === 'horizontal') {
      return val - rect.x - rect.width / 2
    }
    return val - rect.y - rect.height / 2
  })

  const targetSize = useTransform(
    distance,
    [-DOCK_DISTANCE, 0, DOCK_DISTANCE],
    [DOCK_BASE_SIZE, DOCK_MAGNIFICATION, DOCK_BASE_SIZE],
  )
  const animatedSize = useSpring(targetSize, DEFAULT_SPRING)
  // When the user prefers reduced motion, skip the spring entirely and
  // keep every item at base size — no magnification, no animation.
  const size = reduce ? DOCK_BASE_SIZE : animatedSize

  const baseClasses =
    'relative inline-flex items-center justify-center rounded-xl border ' +
    'cursor-pointer outline-none transition-colors ' +
    'shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)] ' +
    'focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ' +
    'focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900'

  const activeClasses = active
    ? 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900 text-blue-600 dark:text-blue-400'
    : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'

  return (
    <motion.div
      ref={ref}
      style={{ width: size, height: size }}
      onHoverStart={() => isHovered.set(1)}
      onHoverEnd={() => isHovered.set(0)}
      onFocus={() => isHovered.set(1)}
      onBlur={() => isHovered.set(0)}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={`${baseClasses} ${activeClasses} ${className}`}
      tabIndex={0}
      role="button"
      aria-haspopup="true"
    >
      {Children.map(children, child =>
        isValidElement(child)
          ? cloneElement(child as ReactElement<{ isHovered?: MotionValue<number>; orientation?: Orientation }>, {
              isHovered,
              orientation,
            })
          : child,
      )}
    </motion.div>
  )
}

interface DockLabelProps {
  children: ReactNode
  isHovered?: MotionValue<number>
  orientation?: Orientation
}

function DockLabel({ children, isHovered, orientation = 'horizontal' }: DockLabelProps) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!isHovered) return
    const unsub = isHovered.on('change', latest => setVisible(latest === 1))
    return () => unsub()
  }, [isHovered])

  // For horizontal docks the label sits above each item; for vertical docks
  // it floats out to the side opposite the screen edge. Without this swap,
  // a left-mounted dock would clip its labels off-screen on the left edge.
  const positionClasses =
    orientation === 'horizontal'
      ? 'left-1/2 -translate-x-1/2 -top-7'
      : 'top-1/2 -translate-y-1/2 left-full ml-2'

  const initial = orientation === 'horizontal' ? { opacity: 0, y: 4 } : { opacity: 0, x: -4 }
  const animate = orientation === 'horizontal' ? { opacity: 1, y: 0 } : { opacity: 1, x: 0 }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={initial}
          animate={animate}
          exit={initial}
          transition={{ duration: 0.15 }}
          className={`pointer-events-none absolute ${positionClasses} whitespace-nowrap rounded-md border border-gray-200 dark:border-gray-800 bg-gray-900 dark:bg-gray-100 px-2 py-0.5 text-xs text-white dark:text-gray-900 shadow`}
          role="tooltip"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

interface DockIconProps {
  children: ReactNode
}

function DockIcon({ children }: DockIconProps) {
  return <div className="flex items-center justify-center">{children}</div>
}

export function Dock({ items, orientation, className = '' }: DockProps) {
  const reduce = useReducedMotion() ?? false
  // Single MotionValue tracking the active axis (pageX for horizontal,
  // pageY for vertical). Out-of-range default keeps every item at base
  // size when the cursor is nowhere near.
  const mouseAxis = useMotionValue(Number.POSITIVE_INFINITY)

  const flexDir = orientation === 'horizontal' ? 'flex-row' : 'flex-col'
  // Outer panel reserves space for the magnified item along the cross-axis
  // so neighbours don't get pushed when one item grows.
  const crossSize = DOCK_MAGNIFICATION + DOCK_PANEL_PADDING * 2

  return (
    <div
      className={`pointer-events-none flex ${flexDir} ${className}`}
      style={
        orientation === 'horizontal'
          ? { height: crossSize }
          : { width: crossSize }
      }
    >
      <div
        onMouseMove={e => {
          mouseAxis.set(orientation === 'horizontal' ? e.pageX : e.pageY)
        }}
        onMouseLeave={() => mouseAxis.set(Number.POSITIVE_INFINITY)}
        className={`pointer-events-auto m-auto flex ${flexDir} items-center gap-2 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur p-2 shadow-lg`}
        role="toolbar"
        aria-label="Application dock"
      >
        {items.map((item, idx) => (
          <DockItem
            key={idx}
            onClick={item.onClick}
            mouseAxis={mouseAxis}
            orientation={orientation}
            reduce={reduce}
            active={item.active}
            className={item.className}
          >
            <DockIcon>{item.icon}</DockIcon>
            <DockLabel>{item.label}</DockLabel>
          </DockItem>
        ))}
      </div>
    </div>
  )
}
